/**
Copyright (c) 2022–2026 Podstack.ai
All rights reserved.
Proprietary and Confidential.
Unauthorized copying of this file, via any medium, is strictly prohibited.
This file is part of the Podstack Platform.
*/

import axios, { AxiosInstance, AxiosError } from 'axios';
import toast from 'react-hot-toast';
import { getGlobalKYCModalHandler } from '../contexts/KYCContext';
import { getGlobalAccountFlaggedHandler } from '../contexts/AccountFlaggedContext';
import { useAuthStore } from '../store/authStore';
import type { CreateSandboxRequest } from '../types/sandbox';
import type {
  BillingDetails,
  BillingDetailsResponse,
  NotificationPreferencesUpdate,
  SpendByProjectResponse,
  Transaction,
  TransactionGroup,
  TransactionQuery,
  TransactionSummary,
  UsageByProductResponse,
  VolumeUsage,
} from '../types';

declare module 'axios' {
  interface AxiosRequestConfig {
    /**
     * Skip the global error toast and KYC modal for this request. Used by
     * optional/background calls (e.g. newer billing endpoints that an older
     * backend may 404 on) that degrade quietly and handle errors themselves.
     */
    silentErrors?: boolean;
  }
}

// Sensitive keywords that should never be exposed in user-facing error messages
const SENSITIVE_KEYWORDS = ['disposable', 'blocked', 'blacklist', 'banned', 'spam', 'unsafe', 'safe_to_send', 'deactivat'];

/**
 * Sanitize backend error messages to prevent leaking security-sensitive information.
 * Replaces messages containing sensitive keywords with a generic error.
 */
function sanitizeErrorMessage(message: string): string {
  const lower = message.toLowerCase();
  if (SENSITIVE_KEYWORDS.some((kw) => lower.includes(kw))) {
    return 'An error occurred. Please try again.';
  }
  return message;
}

// KYC Required error response type
interface KYCRequiredErrorResponse {
  error: string;
  code: string;
  message: string;
  kyc_status?: string;
}

const AUTH_API_URL = process.env.REACT_APP_AUTH_API_URL || 'https://internal.podstack.ai/auth/';
const PLATFORM_API_URL = process.env.REACT_APP_PLATFORM_API_URL || 'https://internal.podstack.ai/platform/';

// options.sendOrgHeader: only auth-service reads X-Organization-Id today, and only
// auth-service's CORS allowlist has been updated for it. Attaching it to every
// cross-origin client (platform, notebooks, sandbox...) triggers a CORS preflight
// those services reject, bricking the dashboard. Only pass this for authApi.
const createApiClient = (baseURL: string, options?: { sendOrgHeader?: boolean }): AxiosInstance => {
  const client = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  client.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    const projectId = useAuthStore.getState().user?.selected_project_id;
    if (projectId && !config.headers['X-Project-Id']) {
      config.headers['X-Project-Id'] = projectId;
    }
    if (options?.sendOrgHeader) {
      const selectedOrgId = localStorage.getItem('selected_org_id');
      if (selectedOrgId) {
        config.headers['X-Organization-Id'] = selectedOrgId;
      }
    }
    return config;
  });

  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError<KYCRequiredErrorResponse>) => {
      // Handle 401 - Unauthorized
      if (error.response?.status === 401) {
        const errorMsg = (error.response?.data as any)?.error || '';

        // Account deactivated - show flagged modal instead of redirect
        if (errorMsg.toLowerCase().includes('deactivat')) {
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          const flaggedHandler = getGlobalAccountFlaggedHandler();
          if (flaggedHandler) {
            flaggedHandler();
          }
          return Promise.reject(error);
        }

        localStorage.removeItem('token');
        localStorage.removeItem('user');
        // Clear stale org selection so the next user (or a re-login by a
        // different account) doesn't inherit the previous user's org header.
        localStorage.removeItem('selected_org_id');
        // Use window.location.origin to preserve the protocol (https)
        window.location.href = `${window.location.origin}/portal/login`;
        return Promise.reject(error);
      }

      // Caller opted out of global error UI (handles/degrades itself).
      if (error.config?.silentErrors) {
        return Promise.reject(error);
      }

      // Handle 403 with KYC_REQUIRED code - Show KYC modal
      if (error.response?.status === 403 && error.response?.data?.code === 'KYC_REQUIRED') {
        const kycModalHandler = getGlobalKYCModalHandler();
        if (kycModalHandler) {
          kycModalHandler(
            error.response.data.kyc_status,
            error.response.data.message
          );
        }
        // Don't show toast for KYC errors - modal handles it
        return Promise.reject(error);
      }

      const message = sanitizeErrorMessage(error.response?.data?.error || 'An error occurred');
      toast.error(message);
      return Promise.reject(error);
    }
  );

  return client;
};

export const authApi = createApiClient(AUTH_API_URL, { sendOrgHeader: true });
export const platformApi = createApiClient(PLATFORM_API_URL);

// Auth APIs
export const authService = {
  // OTP-based authentication (use safeAuthApi to avoid 401 interceptor redirect on login page)
  requestOTP: (email: string, recaptchaToken?: string) =>
    safeAuthApi.post('/auth/login', { email, recaptcha_token: recaptchaToken }),

  verifyOTP: (email: string, otp: string) =>
    safeAuthApi.post('/auth/verify-otp', { email, otp }),

  // Google login
  googleLogin: (code: string, redirectUri: string) =>
    safeAuthApi.post('/auth/google', { code, redirect_uri: redirectUri }),

  // GitHub login
  githubLogin: (code: string) =>
    safeAuthApi.post('/auth/github', { code }),

  // Microsoft login
  microsoftLogin: (code: string, redirectUri: string) =>
    safeAuthApi.post('/auth/microsoft', { code, redirect_uri: redirectUri }),

  logout: () => authApi.post('/auth/logout'),

  getProfile: () => authApi.get('/auth/profile'),

  updateProfile: (data: { name?: string; show_tour?: boolean; selected_project_id?: string; selected_organization_id?: string }) =>
    authApi.put('/auth/profile', data),

  // Public endpoint to validate token (no auth header needed)
  validateToken: (token: string) =>
    authApi.post('/public/validate-token', { token }),

  getAuthConfig: () => safeAuthApi.get('/public/auth-config'),

  ssoLookup: (email: string) =>
    safeAuthApi.get(`/auth/sso/lookup?email=${encodeURIComponent(email)}`),

  ssoCallback: (code: string, state: string) =>
    safeAuthApi.post('/auth/sso/callback', { code, state }),
};

// Project APIs
export const projectService = {
  list: (page = 1, pageSize = 10) =>
    authApi.get(`/projects?page=${page}&page_size=${pageSize}`),

  get: (id: string) => authApi.get(`/projects/${id}`),

  create: (name: string, description: string) =>
    authApi.post('/projects', { name, description }),

  update: (id: string, data: { name?: string; description?: string }) =>
    authApi.put(`/projects/${id}`, data),

  delete: (id: string) => authApi.delete(`/projects/${id}`),

  // Member Management
  getMembers: (id: string) => authApi.get(`/projects/${id}/members`),

  updateMemberPermissions: (projectId: string, userId: string, permissions: {
    can_create: boolean;
    can_read: boolean;
    can_update: boolean;
    can_delete: boolean;
  }) => authApi.put(`/projects/${projectId}/members/${userId}/permissions`, permissions),

  removeMember: (projectId: string, userId: string) =>
    authApi.delete(`/projects/${projectId}/members/${userId}`),

  toggleMemberStatus: (projectId: string, userId: string, isActive: boolean) =>
    authApi.put(`/projects/${projectId}/members/${userId}/status`, { is_active: isActive }),

  // Invitation Management
  inviteMember: (projectId: string, data: {
    email: string;
    role?: string;
    can_create?: boolean;
    can_read?: boolean;
    can_update?: boolean;
    can_delete?: boolean;
  }) => authApi.post(`/projects/${projectId}/invite`, data),

  getInvitations: (projectId: string) =>
    authApi.get(`/projects/${projectId}/invitations`),

  revokeInvitation: (projectId: string, invitationId: string) =>
    authApi.delete(`/projects/${projectId}/invitations/${invitationId}`),

  resendInvitation: (projectId: string, invitationId: string) =>
    authApi.post(`/projects/${projectId}/invitations/${invitationId}/resend`),
};

// Organization APIs
export const organizationService = {
  list: () => authApi.get('/organizations'),
  create: (data: { name: string; slug: string; billing_email?: string }) =>
    authApi.post('/organizations', data),
  get: (id: string) => authApi.get(`/organizations/${id}`),
  update: (id: string, data: { name?: string; billing_email?: string }) =>
    authApi.put(`/organizations/${id}`, data),
  delete: (id: string) => authApi.delete(`/organizations/${id}`),
  transferOwner: (id: string, userId: string) =>
    authApi.post(`/organizations/${id}/transfer-owner`, { user_id: userId }),

  getMembers: (id: string) => authApi.get(`/organizations/${id}/members`),
  updateMemberRole: (id: string, userId: string, role: string) =>
    authApi.put(`/organizations/${id}/members/${userId}/role`, { role }),
  removeMember: (id: string, userId: string) =>
    authApi.delete(`/organizations/${id}/members/${userId}`),
  invite: (id: string, email: string, orgRole: string) =>
    authApi.post(`/organizations/${id}/invite`, { email, org_role: orgRole }),
  getInvitations: (id: string) => authApi.get(`/organizations/${id}/invitations`),

  getDomains: (id: string) => authApi.get(`/organizations/${id}/domains`),
  addDomain: (id: string, domain: string) =>
    authApi.post(`/organizations/${id}/domains`, { domain }),
  verifyDomain: (id: string, domainId: string) =>
    authApi.post(`/organizations/${id}/domains/${domainId}/verify`),
  deleteDomain: (id: string, domainId: string) =>
    authApi.delete(`/organizations/${id}/domains/${domainId}`),

  // safeAuthApi: 404 (no SSO configured yet) is expected - must not fire the global error toast
  getSSO: (id: string) => safeAuthApi.get(`/organizations/${id}/sso`),
  upsertSSO: (id: string, data: { issuer_url: string; client_id: string; client_secret?: string; enabled: boolean }) =>
    authApi.put(`/organizations/${id}/sso`, data),
  deleteSSO: (id: string) => authApi.delete(`/organizations/${id}/sso`),
  testSSO: (id: string) => authApi.post(`/organizations/${id}/sso/test`),
};

// Invitation APIs (public endpoints for accepting invitations)
export const invitationService = {
  // Get invitation details by token (sends OTP to email)
  getInvitation: (token: string) =>
    authApi.get(`/invitations/accept?token=${token}`),

  // Accept invitation while authenticated (login-first flow)
  acceptInvitation: (token: string) =>
    authApi.post('/invitations/accept', { token }),
};

// Container/Pod APIs
export const containerService = {
  list: (projectId?: string) =>
    platformApi.get('/containers', {
      params: { project_id: projectId },
      headers: projectId ? { 'X-Project-Id': projectId } : undefined,
    }),

  // Get all containers for the current user across all projects
  getMyContainers: () => platformApi.get('/containers/my-containers'),

  get: (id: string, projectId: string) =>
    platformApi.get(`/containers/${id}`, { headers: { 'X-Project-Id': projectId } }),

  create: (data: {
    name: string;
    project_id: string;
    image: string;
    cpu_cores: number;
    memory_gb: number;
    storage_gb: number;
    gpu_count?: number;    // Number of physical GPUs (integer, maps to nvidia.com/gpu)
    gpu_cores?: number;    // Percentage per GPU (0-100, maps to nvidia.com/gpumem-percentage)
    gpu_memory_gb?: number;
    gpu_type?: string;
    replicas?: number;
    env_vars?: Record<string, string>;
    ports?: Array<{ container_port: number; protocol?: string; name?: string }>;
    billing_period?: string;
    ssh_key_id?: string;
    command?: string;
    registry_url?: string;
    registry_username?: string;
    registry_password?: string;
  }) => platformApi.post('/containers', data, { headers: { 'X-Project-Id': data.project_id } }),

  delete: (id: string, projectId: string) => 
    platformApi.delete(`/containers/${id}`, { headers: { 'X-Project-Id': projectId } }),

  // body.memory_gb is only honored when relaunching a failed pod (e.g. bumping memory after an OOM kill)
  start: (id: string, projectId: string, body?: { memory_gb?: number }) =>
    platformApi.post(`/containers/${id}/start`, body ?? {}, { headers: { 'X-Project-Id': projectId } }),

  stop: (id: string, projectId: string) => 
    platformApi.post(`/containers/${id}/stop`, {}, { headers: { 'X-Project-Id': projectId } }),

  scale: (id: string, projectId: string, replicas: number) =>
    platformApi.post(`/containers/${id}/scale`, { replicas }, { headers: { 'X-Project-Id': projectId } }),

  update: (id: string, projectId: string, data: {
    ports?: Array<{ container_port: number; protocol?: string; name?: string }>;
  }) => platformApi.put(`/containers/${id}`, data, { headers: { 'X-Project-Id': projectId } }),

  // Get container logs (non-streaming)
  getLogs: (id: string, projectId: string, tail?: number) =>
    platformApi.get(`/containers/${id}/logs`, {
      headers: { 'X-Project-Id': projectId },
      params: { tail }
    }),

  // Get WebSocket URL for streaming logs
  getLogsStreamUrl: (id: string, projectId: string, tail?: number, follow?: boolean) => {
    const platformUrl = process.env.REACT_APP_PLATFORM_API_URL || 'https://internal.podstack.ai/platform/';
    // Convert HTTP URL to WebSocket URL
    const wsUrl = platformUrl.replace(/^http/, 'ws').replace(/\/$/, '');
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    if (tail) params.append('tail', String(tail));
    if (follow !== undefined) params.append('follow', String(follow));
    params.append('token', token || '');
    params.append('project_id', projectId);
    return `${wsUrl}/containers/${id}/logs/stream?${params.toString()}`;
  },

  // Get container resource stats (CPU, memory, storage, GPU)
  getStats: (id: string, projectId: string) =>
    platformApi.get(`/containers/${id}/stats`, { headers: { 'X-Project-Id': projectId } }),

  // Get WebSocket URL for streaming stats
  getStatsStreamUrl: (id: string, projectId: string, interval?: number) => {
    const platformUrl = process.env.REACT_APP_PLATFORM_API_URL || 'https://internal.podstack.ai/platform/';
    // Convert HTTP URL to WebSocket URL
    const wsUrl = platformUrl.replace(/^http/, 'ws').replace(/\/$/, '');
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    if (interval) params.append('interval', String(interval));
    params.append('token', token || '');
    params.append('project_id', projectId);
    return `${wsUrl}/containers/${id}/stats/stream?${params.toString()}`;
  },

  // WebSocket URL for the realtime events channel (container status)
  getEventsUrl: (projectId: string) => {
    const platformUrl = process.env.REACT_APP_PLATFORM_API_URL || 'https://internal.podstack.ai/platform/';
    const wsUrl = platformUrl.replace(/^http/, 'ws').replace(/\/$/, '');
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    params.append('token', token || '');
    params.append('project_id', projectId);
    return `${wsUrl}/ws/events?${params.toString()}`;
  },

  // Get WebSocket URL for interactive terminal
  getTerminalUrl: (id: string, projectId: string) => {
    const platformUrl = process.env.REACT_APP_PLATFORM_API_URL || 'https://internal.podstack.ai/platform/';
    // Convert HTTP URL to WebSocket URL
    const wsUrl = platformUrl.replace(/^http/, 'ws').replace(/\/$/, '');
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    params.append('token', token || '');
    params.append('project_id', projectId);
    return `${wsUrl}/containers/${id}/terminal?${params.toString()}`;
  },

  // Live cost estimate from the backend. Source of truth for the Launch Pod
  // summary - do not recompute on the client when this is available.
  // gpu_fraction = gpu_count × (gpu_cores_percent / 100).
  estimateCost: (
    projectId: string,
    data: {
      cpu_cores: number;
      memory_gb: number;
      storage_gb: number;
      gpu_fraction: number;
      gpu_type?: string;
      billing_period: string;
    }
  ) =>
    platformApi.post('/containers/billing/estimate', data, {
      headers: { 'X-Project-Id': projectId },
    }),

  // Live launch-time capacity for the slider UI. Backend returns the largest
  // CPU / memory / storage that any active cluster carrying the given GPU
  // spec can accept right now (free) and the physical ceiling (ceiling).
  // Pass gpu_count=0 for CPU-only requests.
  getLaunchCapacity: (
    projectId: string,
    params: { gpu_type?: string; gpu_count?: number; gpu_cores_pct?: number }
  ) => {
    // CPU-only requests (gpu_count=0) must not forward GPU-shape params. The
    // create-pod form keeps stale values like gpu_cores=0 in state after the
    // user clears their GPU selection, and the backend's validator rejects
    // gpu_cores_pct=0 with "must be in 1..100".
    const gpuCount = params.gpu_count ?? 0;
    return platformApi.get('/containers/launch-capacity', {
      headers: { 'X-Project-Id': projectId },
      params: {
        gpu_type: gpuCount > 0 ? params.gpu_type || undefined : undefined,
        gpu_count: gpuCount,
        gpu_cores_pct: gpuCount > 0 ? (params.gpu_cores_pct ?? 100) : undefined,
      },
    });
  },
};

// Response shape for containerService.getLaunchCapacity. `ceiling` is the
// physical max - the slider's hard ceiling. `free` is the live-free zone -
// crossing it produces a 409 at submit time, so the UI should warn but not
// block.
export interface LaunchCapacityResources {
  cpu_cores: number;
  memory_gb: number;
  storage_gb: number;
}
export interface LaunchCapacityResponse {
  gpu_type: string;
  gpu_count: number;
  matched_clusters: number;
  free: LaunchCapacityResources;
  ceiling: LaunchCapacityResources;
  gpus_available: number;
  gpus_total: number;
}

// Bucket/Object Storage APIs - requires X-Project-Id header
export const bucketService = {
  list: (projectId: string) =>
    platformApi.get('/buckets', { headers: { 'X-Project-Id': projectId } }),

  get: (id: string, projectId: string) =>
    platformApi.get(`/buckets/${id}`, { headers: { 'X-Project-Id': projectId } }),

  create: (projectId: string, data: {
    name: string;
    description?: string;
    region?: string;
    versioning?: boolean;
    is_public?: boolean;
    max_size_gb?: number;
    billing_period?: string;
  }) => platformApi.post('/buckets', data, { headers: { 'X-Project-Id': projectId } }),

  update: (id: string, projectId: string, data: {
    description?: string;
    versioning?: boolean;
    is_public?: boolean;
    max_size_gb?: number;
  }) => platformApi.put(`/buckets/${id}`, data, { headers: { 'X-Project-Id': projectId } }),

  delete: (id: string, projectId: string, force?: boolean) =>
    platformApi.delete(`/buckets/${id}`, {
      headers: { 'X-Project-Id': projectId },
      params: { force: force ? 'true' : undefined }
    }),

  getCredentials: (id: string, projectId: string) =>
    platformApi.get(`/buckets/${id}/credentials`, { headers: { 'X-Project-Id': projectId } }),

  getUsage: (id: string, projectId: string, from?: string, to?: string) =>
    platformApi.get(`/buckets/${id}/usage`, {
      headers: { 'X-Project-Id': projectId },
      params: { from, to }
    }),

  listObjects: (id: string, projectId: string, prefix?: string, recursive?: boolean, maxKeys?: number) =>
    platformApi.get(`/buckets/${id}/objects`, {
      headers: { 'X-Project-Id': projectId },
      params: { prefix, recursive, max_keys: maxKeys }
    }),

  uploadObject: (id: string, projectId: string, file: File, path?: string) => {
    const formData = new FormData();
    formData.append('file', file);
    return platformApi.post(`/buckets/${id}/objects`, formData, {
      headers: { 'Content-Type': 'multipart/form-data', 'X-Project-Id': projectId },
      params: { path }
    });
  },

  uploadObjectWithProgress: (
    id: string,
    projectId: string,
    file: File,
    path: string | undefined,
    onProgress: (progress: number) => void
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    return platformApi.post(`/buckets/${id}/objects`, formData, {
      headers: { 'Content-Type': 'multipart/form-data', 'X-Project-Id': projectId },
      params: { path },
      onUploadProgress: (progressEvent) => {
        const percentCompleted = progressEvent.total
          ? Math.round((progressEvent.loaded * 100) / progressEvent.total)
          : 0;
        onProgress(percentCompleted);
      },
    });
  },

  downloadObject: (id: string, projectId: string, path: string) =>
    platformApi.get(`/buckets/${id}/objects/download`, {
      headers: { 'X-Project-Id': projectId },
      params: { path },
      responseType: 'blob'
    }),

  deleteObject: (id: string, projectId: string, path: string) =>
    platformApi.delete(`/buckets/${id}/objects`, {
      headers: { 'X-Project-Id': projectId },
      params: { path }
    }),

  createFolder: (id: string, projectId: string, path: string) =>
    platformApi.post(`/buckets/${id}/folders`, { path }, { headers: { 'X-Project-Id': projectId } }),

  getPresignedDownload: (id: string, projectId: string, path: string, expiry?: number) =>
    platformApi.get(`/buckets/${id}/presigned/download`, {
      headers: { 'X-Project-Id': projectId },
      params: { path, expiry }
    }),

  getPresignedUpload: (id: string, projectId: string, path: string, expiry?: number) =>
    platformApi.get(`/buckets/${id}/presigned/upload`, {
      headers: { 'X-Project-Id': projectId },
      params: { path, expiry }
    }),

  sync: (id: string, projectId: string) =>
    platformApi.post(`/buckets/${id}/sync`, {}, { headers: { 'X-Project-Id': projectId } }),

  // Multipart/Chunked upload operations
  initiateMultipartUpload: (id: string, projectId: string, path: string, contentType?: string, fileSize?: number) =>
    platformApi.post(`/buckets/${id}/multipart/initiate`,
      { path, content_type: contentType, file_size: fileSize },
      { headers: { 'X-Project-Id': projectId } }
    ),

  uploadPart: (
    id: string,
    projectId: string,
    uploadId: string,
    partNumber: number,
    chunk: Blob,
    path: string,
    onProgress?: (progress: number) => void
  ) => {
    const formData = new FormData();
    formData.append('chunk', chunk);
    return platformApi.put(
      `/buckets/${id}/multipart/${uploadId}/parts/${partNumber}`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data', 'X-Project-Id': projectId },
        params: { path },
        onUploadProgress: (progressEvent) => {
          if (onProgress && progressEvent.total) {
            const percent = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            onProgress(percent);
          }
        },
      }
    );
  },

  completeMultipartUpload: (
    id: string,
    projectId: string,
    uploadId: string,
    path: string,
    parts: { part_number: number; etag: string }[]
  ) =>
    platformApi.post(
      `/buckets/${id}/multipart/${uploadId}/complete`,
      { path, parts },
      { headers: { 'X-Project-Id': projectId } }
    ),

  abortMultipartUpload: (id: string, projectId: string, uploadId: string, path: string) =>
    platformApi.delete(`/buckets/${id}/multipart/${uploadId}`, {
      headers: { 'X-Project-Id': projectId },
      params: { path }
    }),

  listUploadParts: (id: string, projectId: string, uploadId: string, path: string) =>
    platformApi.get(`/buckets/${id}/multipart/${uploadId}/parts`, {
      headers: { 'X-Project-Id': projectId },
      params: { path }
    }),

  // Live cost estimate from the backend. Pulls the current DB rate + any period
  // discount - never hardcode rates in the UI.
  estimateCost: (
    projectId: string,
    data: { size_gb: number; billing_period: string }
  ) =>
    platformApi.post('/buckets/billing/estimate', data, {
      headers: { 'X-Project-Id': projectId },
    }),
};

// Volume/Storage APIs (TrueNAS NFS) - requires X-Project-Id header
export const volumeService = {
  list: (projectId: string) =>
    platformApi.get('/storage', { headers: { 'X-Project-Id': projectId } }),

  get: (id: string, projectId: string) =>
    platformApi.get(`/storage/${id}`, { headers: { 'X-Project-Id': projectId } }),

  create: (projectId: string, data: {
    name: string;
    quota_gb?: number;
    nfs_enabled?: boolean;
    billing_period?: string;
  }) => platformApi.post('/storage', data, { headers: { 'X-Project-Id': projectId } }),

  updateQuota: (id: string, projectId: string, quota_gb: number) =>
    platformApi.put(`/storage/${id}/quota`, { quota_gb }, { headers: { 'X-Project-Id': projectId } }),

  delete: (id: string, projectId: string) =>
    platformApi.delete(`/storage/${id}`, { headers: { 'X-Project-Id': projectId } }),

  // Returns VolumeUsage: { size_bytes (quota, 0 = unlimited), used_bytes,
  // available_bytes, usage_percent, last_updated }. from/to are currently
  // ignored by the backend (point-in-time snapshot of the last TrueNAS sync).
  // list/get also return used_bytes, attached_to and cost_this_month inline.
  getUsage: (id: string, projectId: string, from?: string, to?: string) =>
    platformApi.get<VolumeUsage>(`/storage/${id}/usage`, {
      headers: { 'X-Project-Id': projectId },
      params: { from, to }
    }),

  sync: (id: string, projectId: string) =>
    platformApi.post(`/storage/${id}/sync`, {}, { headers: { 'X-Project-Id': projectId } }),

  getNFSConfig: (projectId: string) =>
    platformApi.get('/storage/nfs-config', { params: { project_id: projectId } }),

  // Live cost estimate from the backend - source of truth for the create dialog.
  // Don't hardcode rates in the UI; admins can change them via
  // PUT /api/v1/admin/pricing/resources/nfs.
  estimateCost: (
    projectId: string,
    data: { size_gb: number; billing_period: string }
  ) =>
    platformApi.post('/storage/billing/estimate', data, {
      headers: { 'X-Project-Id': projectId },
    }),
};

// Template APIs
export const templateService = {
  list: (type?: 'pod' | 'vm') => platformApi.get('/templates', { params: type ? { type } : undefined }),
  getCatalog: () => platformApi.get('/template-catalog'),

  get: (id: string, projectId?: string) => 
    platformApi.get(`/templates/${id}`, { 
      headers: projectId ? { 'X-Project-Id': projectId } : undefined 
    }),

  getMyTemplates: (projectId?: string) => {
    const headers = projectId ? { 'X-Project-Id': projectId } : undefined;
    return platformApi.get('/templates/my-templates', { headers });
  },

  getProjectTemplates: (projectId: string) =>
    platformApi.get(`/projects/${projectId}/templates`, { 
      headers: { 'X-Project-Id': projectId } 
    }),

  search: (query: string, templateType?: string, gpuType?: string) =>
    platformApi.post('/templates/search', { query, template_type: templateType, gpu_type: gpuType }),

  create: (data: {
    name: string;
    description: string;
    type: string;
    scope: string;
    project_id?: string;
    image_name: string;
    image_url?: string;
    cpu_cores?: number;
    memory_gb?: number;
    storage_gb?: number;
    gpu_cores?: number;
    gpu_type?: string;
    env_variables?: Record<string, string>;
    ports?: number[];
    init_command?: string;
    tags?: string[];
  }) => platformApi.post('/templates', data, { 
    headers: data.project_id ? { 'X-Project-Id': data.project_id } : undefined 
  }),

  update: (id: string, projectId: string | undefined, data: Partial<{
    name: string;
    description: string;
    scope: string;
    image: string;
    cpu_cores: number;
    memory_gb: number;
    gpu_count: number;
    gpu_type: string;
    env_vars: Record<string, string>;
    ports: number[];
    volumes: string[];
    startup_command: string;
  }>) => platformApi.put(`/templates/${id}`, data, { 
    headers: projectId ? { 'X-Project-Id': projectId } : undefined 
  }),

  delete: (id: string, projectId?: string) => 
    platformApi.delete(`/templates/${id}`, { 
      headers: projectId ? { 'X-Project-Id': projectId } : undefined 
    }),
};

// Pricing APIs
export const pricingService = {
  getGPUPricing: () => platformApi.get('/pricing/gpu'),
  getGPUAvailability: () => platformApi.get('/pricing/gpu/availability'),
  getResourcePricing: () => platformApi.get('/pricing/resources'),
};

// Currency Settings APIs (public - read only)
export const currencyService = {
  // Get current currency settings (USD to INR rate, GST rate, markup percentage)
  get: () => platformApi.get('/settings/currency'),
};

// Wallet APIs
/** Minutes east of UTC for the browser's zone (IST → 330). */
export const localTzOffset = () => -new Date().getTimezoneOffset();

export interface ReportPeriodParams {
  period?: 'month' | 'last_month' | '7d' | '30d' | '90d';
  from?: string;
  to?: string;
}

const csv = (v?: string[]) => (v && v.length ? v.join(',') : undefined);

/** Serialise a TransactionQuery into query params (undefined keys are dropped by axios). */
const transactionParams = (q: TransactionQuery) => ({
  page: q.page,
  page_size: q.page_size,
  type: csv(q.type),
  reference_type: csv(q.reference_type),
  product: csv(q.product),
  reference_id: q.reference_id || undefined,
  from: q.from || undefined,
  to: q.to || undefined,
  tz_offset: localTzOffset(), // day boundaries for YYYY-MM-DD bounds + grouping
  project_id: q.project_id || undefined,
  q: q.q || undefined,
  include_summary: q.include_summary ? 'true' : undefined,
});

export const walletService = {
  get: () => platformApi.get('/wallet'),

  // Returns the wallet summary of the project's billing owner (the user who pays
  // for the project). Members can call this to see remaining balance on
  // projects they belong to but don't own.
  getProjectBalance: (projectId: string) =>
    platformApi.get('/wallet/project-balance', {
      headers: { 'X-Project-Id': projectId },
    }),

  // Get user expenditure summary for dashboard
  getExpenditure: () => platformApi.get('/wallet/expenditure'),

  topUp: (amount: number, payment_method?: string) =>
    platformApi.post('/wallet/topup', { amount, payment_method }),

  verifyPayment: (order_id: string, payment_id: string, signature: string) =>
    platformApi.post('/wallet/payment/verify', {
      razorpay_order_id: order_id,
      razorpay_payment_id: payment_id,
      razorpay_signature: signature
    }),

  getTransactions: (page = 1, pageSize = 20) =>
    platformApi.get(`/wallet/transactions?page=${page}&page_size=${pageSize}`),

  /**
   * Filtered transaction listing. All filters are optional; pass
   * include_summary to get totals for the filtered set. Older backends ignore
   * the filters and omit `summary` - callers use that to detect legacy mode.
   */
  getTransactionsFiltered: (query: TransactionQuery = {}) =>
    platformApi.get<{
      transactions: Transaction[];
      total: number;
      page: number;
      page_size: number;
      summary?: TransactionSummary;
    }>('/wallet/transactions', { params: transactionParams(query), silentErrors: true }),

  /** One row per day + resource. 404 on older backends. */
  getTransactionGroups: (query: TransactionQuery = {}) =>
    platformApi.get<{
      groups: TransactionGroup[];
      total: number;
      page: number;
      page_size: number;
      summary?: TransactionSummary;
    }>('/wallet/transactions/grouped', { params: transactionParams(query), silentErrors: true }),

  /** Spend per product category; defaults to month-to-date. 404 on older backends. */
  getUsageByProduct: (params: ReportPeriodParams = { period: 'month' }) =>
    platformApi.get<UsageByProductResponse>('/wallet/usage-by-product', {
      params: { tz_offset: localTzOffset(), ...params },
      silentErrors: true,
    }),

  /** Spend attributed per project; defaults to month-to-date. 404 on older backends. */
  getSpendByProject: (params: ReportPeriodParams = { period: 'month' }) =>
    platformApi.get<SpendByProjectResponse>('/wallet/spend-by-project', {
      params: { tz_offset: localTzOffset(), ...params },
      silentErrors: true,
    }),

  upgradeToPostpaid: (userId: string) =>
    platformApi.post(`/admin/wallet/${userId}/upgrade-postpaid`),

  // Saved Payment Methods
  getPaymentMethods: () => platformApi.get('/wallet/payment-methods'),

  setDefaultPaymentMethod: (id: string) =>
    platformApi.put(`/wallet/payment-methods/${id}/default`),

  deletePaymentMethod: (id: string) =>
    platformApi.delete(`/wallet/payment-methods/${id}`),

  syncSavedCards: () => platformApi.post('/wallet/payment-methods/sync'),

  // Auto-debit
  enableAutoDebit: (payment_method_id: string) =>
    platformApi.post('/wallet/auto-debit/enable', { payment_method_id }),

  disableAutoDebit: () => platformApi.post('/wallet/auto-debit/disable'),

  // Mandate Registration
  initiateMandateRegistration: () =>
    platformApi.post('/wallet/mandate/register'),

  completeMandateRegistration: (order_id: string, payment_id: string, signature: string) =>
    platformApi.post('/wallet/mandate/complete', {
      razorpay_order_id: order_id,
      razorpay_payment_id: payment_id,
      razorpay_signature: signature
    }),

  // PayPal APIs
  createPayPalOrder: (amountUSD: number) =>
    platformApi.post('/wallet/paypal/create-order', { amount_usd: amountUSD }),

  capturePayPalOrder: (orderId: string) =>
    platformApi.post('/wallet/paypal/capture-order', { order_id: orderId }),

  getInternationalPaymentMethods: () =>
    platformApi.get('/wallet/payment-methods/international'),

  // Refund APIs
  getRefundEligibility: () => platformApi.get('/wallet/refund/eligibility'),

  getRefundEligibilityDetails: () => platformApi.get('/wallet/refund/eligibility/details'),

  requestRefund: (request: {
    amount: number;
    reason?: string;
    payment_mode: 'upi' | 'bank' | 'paypal' | 'crypto';
    upi_id?: string;
    bank_account_no?: string;
    bank_ifsc?: string;
    bank_account_name?: string;
    paypal_email?: string;
    crypto_wallet_address?: string;
    crypto_network?: string;
  }) => platformApi.post('/wallet/refund/request', request),

  getRefundHistory: () => platformApi.get('/wallet/refund/history'),
};

// KYC APIs
export const kycService = {
  submit: (data: {
    kyc_type: string;
    full_name?: string;
    date_of_birth?: string;
    pan_number?: string;
    aadhaar_number?: string;
    organisation_name?: string;
    gst_number?: string;
    cin_number?: string;
    address_line1: string;
    address_line2?: string;
    city: string;
    state: string;
    pin_code: string;
  }) => platformApi.post('/kyc', data),

  getStatus: () => platformApi.get('/kyc'),

  // Get available KYC providers
  getProviders: () => platformApi.get('/kyc/providers'),

  // Didit KYC methods
  createDiditSession: () => platformApi.post('/kyc/didit/session'),
  getDiditStatus: () => platformApi.get('/kyc/didit/status'),
};

// Invoice "Billed to" details (legal name, GSTIN, address). 404 on older backends.
export const billingDetailsService = {
  get: () => platformApi.get<BillingDetailsResponse>('/billing-details', { silentErrors: true }),
  update: (details: Partial<BillingDetails>) =>
    platformApi.put<BillingDetailsResponse>('/billing-details', details, { silentErrors: true }),
};

// Invoice APIs
export const invoiceService = {
  list: (page = 1, pageSize = 10) =>
    platformApi.get(`/invoices?page=${page}&page_size=${pageSize}`),

  get: (id: string) => platformApi.get(`/invoices/${id}`),

  pay: (id: string, payment_method?: string) =>
    platformApi.post(`/invoices/${id}/pay`, { payment_method }),

  generate: (startDate: string, endDate: string) =>
    platformApi.post('/invoices/generate', { start_date: startDate, end_date: endDate }),
};

// VM APIs - requires X-Project-Id header
export const vmService = {
  list: (projectId: string) =>
    platformApi.get('/vms', { headers: { 'X-Project-Id': projectId } }),

  get: (id: string, projectId: string) =>
    platformApi.get(`/vms/${id}`, { headers: { 'X-Project-Id': projectId } }),

  create: (projectId: string, data: {
    name: string;
    cpu_cores: number;
    memory_gb: number;
    storage_gb: number;
    gpu_count?: number;
    gpu_type?: string;
    image?: string;
    billing_period?: string;
    ssh_key_id?: string;
  }) => platformApi.post('/vms', data, { headers: { 'X-Project-Id': projectId } }),

  delete: (id: string, projectId: string) =>
    platformApi.delete(`/vms/${id}`, { headers: { 'X-Project-Id': projectId } }),

  start: (id: string, projectId: string) =>
    platformApi.post(`/vms/${id}/start`, {}, { headers: { 'X-Project-Id': projectId } }),

  stop: (id: string, projectId: string) =>
    platformApi.post(`/vms/${id}/stop`, {}, { headers: { 'X-Project-Id': projectId } }),
};

// Waitlist APIs
export const waitlistService = {
  join: (data: {
    gpu_type: string;
    gpu_count?: number;
    gpu_fraction?: number;
    cpu_cores?: number;
    memory_gb?: number;
    gpu_memory_gb?: number;
    resource_type?: string;
    project_id?: string;
  }) => platformApi.post('/waitlist', data),

  getMyEntries: () => platformApi.get('/waitlist'),

  get: (id: string) => platformApi.get(`/waitlist/${id}`),

  cancel: (id: string) => platformApi.delete(`/waitlist/${id}`),
};

// SSH Key APIs - requires X-Project-Id header
export const sshKeyService = {
  list: (projectId?: string) =>
    platformApi.get('/ssh-keys', {
      headers: projectId ? { 'X-Project-Id': projectId } : undefined,
    }),

  get: (id: string, projectId?: string) =>
    platformApi.get(`/ssh-keys/${id}`, {
      headers: projectId ? { 'X-Project-Id': projectId } : undefined,
    }),

  create: (name: string, publicKey: string, projectId?: string) =>
    platformApi.post('/ssh-keys', { name, public_key: publicKey }, {
      headers: projectId ? { 'X-Project-Id': projectId } : undefined,
    }),

  generate: (name: string, keyType: 'rsa' | 'ed25519', keySize?: number, projectId?: string) =>
    platformApi.post('/ssh-keys/generate', { name, key_type: keyType, key_size: keySize }, {
      headers: projectId ? { 'X-Project-Id': projectId } : undefined,
    }),

  delete: (id: string, projectId?: string) =>
    platformApi.delete(`/ssh-keys/${id}`, {
      headers: projectId ? { 'X-Project-Id': projectId } : undefined,
    }),

  setDefault: (id: string, projectId?: string) =>
    platformApi.put(`/ssh-keys/${id}/default`, {}, {
      headers: projectId ? { 'X-Project-Id': projectId } : undefined,
    }),
};

// API Token APIs (for JupyterHub, CLI, programmatic access)
export const apiTokenService = {
  list: () => authApi.get('/api-tokens'),

  create: (name: string, expiresIn: string) =>
    authApi.post('/api-tokens', { name, expires_in: expiresIn }),

  revoke: (id: string) => authApi.delete(`/api-tokens/${id}`),

  // Public endpoint for validation (used by JupyterHub authenticator)
  validate: (token: string) =>
    authApi.post('/public/validate-api-token', { token }),
};

// Create a "safe" API client that doesn't redirect on 401 (for optional endpoints)
const createSafeApiClient = (baseURL: string): AxiosInstance => {
  const client = axios.create({
    baseURL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  client.interceptors.request.use((config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  // No 401 redirect - just reject the promise
  client.interceptors.response.use(
    (response) => response,
    (error: AxiosError) => Promise.reject(error)
  );

  return client;
};

const safeAuthApi = createSafeApiClient(AUTH_API_URL);

// Profile APIs (using safe client to avoid logout on missing endpoints)
export const profileService = {
  get: () => safeAuthApi.get('/profile'),

  update: (data: { name?: string; phone?: string; timezone?: string }) =>
    safeAuthApi.put('/profile', data),

  uploadPhoto: (file: File) => {
    const formData = new FormData();
    formData.append('photo', file);
    return safeAuthApi.post('/profile/photo', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },

  deletePhoto: () => safeAuthApi.delete('/profile/photo'),
};

// Preferences APIs (using safe client to avoid logout on missing endpoints)
export const preferencesService = {
  getAll: () => safeAuthApi.get('/preferences'),

  getNotifications: () => safeAuthApi.get('/preferences/notifications'),

  updateNotifications: (data: NotificationPreferencesUpdate) => safeAuthApi.put('/preferences/notifications', data),

  getAppearance: () => safeAuthApi.get('/preferences/appearance'),

  updateAppearance: (data: {
    theme?: string;
    compact_mode?: boolean;
    sidebar_collapsed?: boolean;
    remember_sidebar_state?: boolean;
    preferred_currency?: string;
    show_prices_per_minute?: boolean;
  }) => safeAuthApi.put('/preferences/appearance', data),
};
// Security APIs (using safe client to avoid logout on missing endpoints)
export const securityService = {
  getInfo: () => safeAuthApi.get('/security'),

  getSessions: () => safeAuthApi.get('/security/sessions'),

  revokeSession: (id: string) => safeAuthApi.delete(`/security/sessions/${id}`),

  revokeAllOtherSessions: () => safeAuthApi.post('/security/sessions/revoke-others'),
};

// Notification APIs (using safe client to avoid logout on missing endpoints)
export const notificationService = {
  // Get notifications (filtered by user's enabled preferences)
  getAll: () => safeAuthApi.get('/notifications'),

  // Get unread notification count
  getUnreadCount: () => safeAuthApi.get('/notifications/unread-count'),

  // Mark a notification as read
  markAsRead: (id: string) => safeAuthApi.post(`/notifications/${id}/read`),

  // Mark all notifications as read
  markAllAsRead: () => safeAuthApi.post('/notifications/read-all'),

  // Delete a notification
  delete: (id: string) => safeAuthApi.delete(`/notifications/${id}`),
};

// Coupon APIs (for customer coupon redemption)
export const couponService = {
  // Validate a coupon code before applying
  validate: (code: string, purchaseAmount?: number) =>
    platformApi.post('/coupons/validate', { code, purchase_amount: purchaseAmount }),

  // Redeem a coupon code (applies discount or adds credits)
  redeem: (code: string, purchaseAmount?: number, orderId?: string) =>
    platformApi.post('/coupons/redeem', { code, purchase_amount: purchaseAmount, order_id: orderId }),

  // Get user's coupon redemption history
  getMyRedemptions: () => platformApi.get('/coupons/my-redemptions'),
};

// Audit Log APIs (for project audit logs)
export const auditLogService = {
  // Get paginated audit logs for a project
  list: (projectId: string, params?: { page?: number; page_size?: number; action?: string; resource_type?: string }) => {
    const searchParams = new URLSearchParams();
    if (params?.page) searchParams.append('page', String(params.page));
    if (params?.page_size) searchParams.append('page_size', String(params.page_size));
    if (params?.action) searchParams.append('action', params.action);
    if (params?.resource_type) searchParams.append('resource_type', params.resource_type);
    return platformApi.get(`/audit-logs?${searchParams.toString()}`, { headers: { 'X-Project-Id': projectId } });
  },

  // Get WebSocket URL for streaming audit logs
  getStreamUrl: (projectId: string) => {
    const platformUrl = process.env.REACT_APP_PLATFORM_API_URL || 'https://internal.podstack.ai/platform/';
    // Convert HTTP URL to WebSocket URL
    const wsUrl = platformUrl.replace(/^http/, 'ws').replace(/\/$/, '');
    const token = localStorage.getItem('token');
    const params = new URLSearchParams();
    params.append('token', token || '');
    params.append('project_id', projectId);
    return `${wsUrl}/audit-logs/stream?${params.toString()}`;
  },
};

// Notebooks API URL (Serverless GPU)
// Note: When PODSTACK_PLATFORM=true, notebooks service uses backend-core for auth, projects, and billing.
// This service only handles notebook-specific CRUD operations.
// Sandbox API URL
const SANDBOX_API_URL = process.env.REACT_APP_SANDBOX_API_URL || 'https://cloud.podstack.ai/sandbox';
const sandboxApi = createApiClient(SANDBOX_API_URL);

const NOTEBOOKS_API_URL = process.env.REACT_APP_NOTEBOOKS_API_URL || 'https://cloud.podstack.ai/notebooks/api';
const notebooksApi = createApiClient(NOTEBOOKS_API_URL);

// Notebook Types
export interface Notebook {
  id: string;
  project_id: string;
  user_id: string;
  name: string;
  status: 'pending' | 'starting' | 'running' | 'stopping' | 'stopped' | 'failed' | 'terminated';
  url?: string;
  created_at: string;
  updated_at: string;
  started_at?: string;
  stopped_at?: string;
  idle_shutdown_enabled: boolean;
  idle_timeout_minutes: number;
}

// Notebooks Service (Serverless GPU)
// Only handles notebook CRUD operations - auth, projects, and billing are managed by backend-core
export const notebookService = {
  // List all notebooks for a project (project comes from backend-core projectService)
  list: (projectId: string) =>
    notebooksApi.get(`/v1/notebooks?project_id=${projectId}`),

  // Get a specific notebook
  get: (notebookId: string) =>
    notebooksApi.get(`/v1/notebooks/${notebookId}`),

  // Create a new notebook (billing handled by backend-core wallet service)
  create: (projectId: string, name: string) =>
    notebooksApi.post('/v1/notebooks', { project_id: projectId, name }),

  // Start a notebook (billing charges handled by backend-core)
  start: (notebookId: string) =>
    notebooksApi.post(`/v1/notebooks/${notebookId}/start`),

  // Stop a notebook (billing stops via backend-core)
  stop: (notebookId: string) =>
    notebooksApi.post(`/v1/notebooks/${notebookId}/stop`),

  // Delete a notebook
  delete: (notebookId: string) =>
    notebooksApi.delete(`/v1/notebooks/${notebookId}`),

  // Update idle shutdown settings
  updateIdleSettings: (notebookId: string, enabled: boolean, timeoutMinutes: number) =>
    notebooksApi.put(`/v1/notebooks/${notebookId}/idle-settings`, {
      idle_shutdown_enabled: enabled,
      idle_timeout_minutes: timeoutMinutes,
    }),
};

// Sandbox Image Service (public catalog)
export const sandboxImageService = {
  list: () => sandboxApi.get('/api/v1/sandbox-images'),
};

// Sandbox Service (Code Execution Sandboxes)
export const sandboxService = {
  list: (projectId?: string, page = 1, pageSize = 20) =>
    sandboxApi.get('/api/v1/sandboxes', {
      params: { project_id: projectId, page, page_size: pageSize },
    }),

  get: (id: string) =>
    sandboxApi.get(`/api/v1/sandboxes/${id}`),

  create: (data: CreateSandboxRequest) =>
    sandboxApi.post('/api/v1/sandboxes', data),

  delete: (id: string) =>
    sandboxApi.delete(`/api/v1/sandboxes/${id}`),

  // Bulk-removes the caller's ended (Terminated/Failed) sandboxes in a project
  // from history. Live sandboxes are untouched. Returns { deleted: number }.
  // Older sandbox-service builds return 404/405 for this route.
  clearTerminated: (projectId: string) =>
    sandboxApi.delete<{ deleted: number }>('/api/v1/sandboxes', {
      params: { status: 'terminated', project_id: projectId },
      headers: { 'X-Project-ID': projectId },
    }),

  pause: (id: string) =>
    sandboxApi.post(`/api/v1/sandboxes/${id}/pause`),

  resume: (id: string) =>
    sandboxApi.post(`/api/v1/sandboxes/${id}/resume`),

  renewExpiration: (id: string, additionalSeconds: number) =>
    sandboxApi.post(`/api/v1/sandboxes/${id}/renew-expiration`, { additional_seconds: additionalSeconds }),

  executeCode: (id: string, language: string, code: string) =>
    sandboxApi.post(`/api/v1/sandboxes/${id}/code`, { language, code }),

  runCommand: async (id: string, command: string, timeout?: number): Promise<{ stdout: string; stderr: string; exit_code: number }> => {
    const token = localStorage.getItem('token');
    const baseURL = process.env.REACT_APP_SANDBOX_API_URL || 'https://cloud.podstack.ai/sandbox';
    const res = await fetch(`${baseURL}/api/v1/sandboxes/${id}/command`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ command, timeout }),
    });

    if (!res.ok) {
      const errBody = await res.json().catch(() => ({ error: res.statusText }));
      throw { response: { data: errBody, status: res.status } };
    }

    const text = await res.text();
    let stdout = '';
    let stderr = '';
    let exitCode = 0;

    // Parse response lines - execd returns raw JSON lines (not SSE data: prefixed)
    // Format: {"type":"stdout|stderr|error|execution_complete","text":"...","timestamp":...}
    for (const rawLine of text.split('\n')) {
      const line = rawLine.trim();
      if (!line) continue;
      // Strip SSE "data: " prefix if present, otherwise use raw line
      const jsonStr = line.startsWith('data: ') ? line.slice(6) : line;
      try {
        const payload = JSON.parse(jsonStr);
        if (payload.type === 'stdout' && payload.text) stdout += payload.text;
        else if (payload.type === 'stderr' && payload.text) stderr += payload.text;
        else if (payload.type === 'error' && payload.error) stderr += payload.error;
        else if (payload.type === 'execution_complete') {
          if (payload.exit_code !== undefined) exitCode = payload.exit_code;
        }
        // Fallback for other formats
        else if (payload.stdout) stdout += payload.stdout;
        else if (payload.stderr) stderr += payload.stderr;
        else if (payload.output) stdout += payload.output;
      } catch {
        // Skip non-JSON lines (empty, ping, etc.)
      }
    }

    return { stdout, stderr, exit_code: exitCode };
  },

  getMetrics: (id: string) =>
    sandboxApi.get(`/api/v1/sandboxes/${id}/metrics`),

  getBilling: (id: string) =>
    sandboxApi.get(`/api/v1/sandboxes/${id}/billing/summary`),

  estimateCost: (resources: { cpu: string; memory: string; gpu_count?: number; gpu_type?: string }) =>
    sandboxApi.post('/api/v1/sandboxes/billing/estimate', resources),
};

// Managed MLOps APIs (managed MLflow/Kubeflow Pipelines/DVC/Metaflow)
export const managedServiceApi = {
  getCatalog: () => platformApi.get('/managed-services/catalog'),

  order: (data: {
    product: string;
    tier: string;
    storage_gb: number;
    project_id: string;
    name: string;
  }) => platformApi.post('/managed-services', data, { headers: { 'X-Project-Id': data.project_id } }),

  list: (projectId?: string) =>
    platformApi.get('/managed-services', {
      headers: projectId ? { 'X-Project-Id': projectId } : undefined,
    }),

  get: (id: string) => platformApi.get(`/managed-services/${id}`),

  getCredentials: (id: string) => platformApi.get(`/managed-services/${id}/credentials`),

  resume: (id: string) => platformApi.post(`/managed-services/${id}/resume`, {}),

  remove: (id: string) => platformApi.delete(`/managed-services/${id}`),
};


// Builder credits application service
export interface BuilderCreditsApplication {
  email: string;
  project_description: string;
  github_url?: string;
  colab_url?: string;
  linkedin_url?: string;
  phone?: string;
}

export const builderCreditsService = {
  apply: (data: BuilderCreditsApplication) => platformApi.post('/public/builder-credits/apply', data),
};
